import PropTypes from 'prop-types';

import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, useMediaQuery } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';

import { getTimePickerId } from '../utils';

import { ButtonLink } from '../../Button';
import Tooltip from '../../Tooltip';
import DayCheckbox from './ScheduleDayCheckbox';
import TimePicker from './ScheduleTimePicker';
import TimeRangeRadios from './ScheduleTimeRangeRadios';

const StyledAddButton = styled(ButtonLink)(({ theme }) => ({
  fontSize: '1.4rem',
  textAlign: 'center',
  '&:disabled': {
    cursor: 'default',
    textDecoration: 'none',
    color: alpha(theme.palette.custom.link, 0.8),
  },
}));

const StyledRemoveButton = styled(ButtonLink)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontSize: '1.4rem',
}));

const StyledContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'endTime',
})(({ theme, endTime }) => ({
  display: 'grid',
  gridTemplateColumns: `12rem 10rem repeat(4, 1fr)`,
  gridTemplateRows: `4rem repeat(${endTime.length}, 1fr)`,
  columnGap: '1rem',
  marginBottom: '1rem',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: `10rem repeat(4, 1fr)`,
    gridTemplateRows: `4rem 4rem repeat(${endTime.length}, 1fr)`,
  },
}));

const StyledDayItem = styled('div')(({ theme }) => ({
  gridColumn: '1 / span 1',
  [theme.breakpoints.down('md')]: {
    gridColumn: '1 / -1',
  },
}));

const StyledTimeRangeItem = styled('div')(({ theme }) => ({
  gridColumn: '2 / -1',
  [theme.breakpoints.down('md')]: {
    gridColumn: '1 / -1',
    gridRow: '2',
  },
}));

const StyledStartTimeItem = styled('div', {
  shouldForwardProp: (prop) => prop !== 'endTime',
})(({ theme, endTime }) => ({
  gridColumn: '2 / span 1',
  gridRow: `2 / span ${endTime.length}`,
  [theme.breakpoints.down('md')]: {
    gridColumn: '1 / span 1',
    gridRow: `3 / span ${endTime.length}`,
  },
}));

const StyledEndTimeItem = styled('div', {
  shouldForwardProp: (prop) => prop !== 'endTime',
})(({ theme, endTime }) => ({
  gridColumn: '3 / -1',
  gridRow: `2 / span ${endTime.length}`,
  [theme.breakpoints.down('md')]: {
    gridColumn: '2 / -1',
    gridRow: `3 / span ${endTime.length}`,
  },
}));

const getShowAddTimeSlot = (timeSlot, allTimeSlots, i) =>
  !timeSlot?.disabled && i === Number(allTimeSlots?.length) - 1;

const ScheduleRow = (props) => {
  const theme = useTheme();
  const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    checkbox,
    radio,
    startTime,
    endTime,
    isMultiTimeSlot,
    maxTimeSlotLength,
    formErrors,
  } = props;

  return (
    <StyledContainer endTime={endTime}>
      <StyledDayItem>
        <DayCheckbox checkbox={checkbox} />
      </StyledDayItem>
      <StyledTimeRangeItem>
        <TimeRangeRadios radio={radio} />
      </StyledTimeRangeItem>

      <StyledStartTimeItem endTime={endTime}>
        {startTime?.map((timeSlot, i) => {
          const timePickerId = getTimePickerId(timeSlot.name, i, true);
          const hasError = Boolean(formErrors?.[timePickerId]);

          return (
            <TimePicker
              key={timeSlot.name + i}
              placeholder="Start"
              time={timeSlot}
              index={i}
              isStartTime
              hasError={hasError}
            />
          );
        })}
      </StyledStartTimeItem>
      <StyledEndTimeItem endTime={endTime}>
        {endTime?.map((timeSlot, i) => {
          const { onAddTimeInterval, onRemoveTimeInterval, name } = timeSlot;

          const timePickerId = getTimePickerId(name, i, false);
          const hasError = Boolean(formErrors?.[timePickerId]);
          const hasAnyError =
            hasError || formErrors?.[getTimePickerId(name, i, true)];

          const showAddTimeSlot =
            isMultiTimeSlot && getShowAddTimeSlot(timeSlot, endTime, i);
          const isMaxTimeSlots = endTime.length >= maxTimeSlotLength;
          const showRemoveTimeSlot = i !== 0;
          const isAddTimeSlotDisabled =
            !startTime[i]?.value ||
            !endTime[i]?.value ||
            isMaxTimeSlots ||
            hasAnyError;

          return (
            <Box
              sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}
              key={timeSlot.name + i}
            >
              <TimePicker
                placeholder="End"
                time={timeSlot}
                index={i}
                isStartTime={false}
                hasError={hasError}
              />
              {showAddTimeSlot && (
                <>
                  <StyledAddButton
                    disabled={isAddTimeSlotDisabled}
                    onClick={() => onAddTimeInterval(name)}
                  >
                    <Tooltip
                      tooltip={
                        isMaxTimeSlots &&
                        `The maximum number of time slots is ${maxTimeSlotLength}`
                      }
                    >
                      + Add time slot
                    </Tooltip>
                  </StyledAddButton>
                  {showRemoveTimeSlot &&
                    (isSmScreen ? (
                      <IconButton onClick={() => onRemoveTimeInterval(name, i)}>
                        <DeleteIcon fontSize="small" color="secondary" />
                      </IconButton>
                    ) : (
                      <StyledRemoveButton
                        onClick={() => onRemoveTimeInterval(name, i)}
                      >
                        Remove
                      </StyledRemoveButton>
                    ))}
                </>
              )}
            </Box>
          );
        })}
      </StyledEndTimeItem>
    </StyledContainer>
  );
};

ScheduleRow.defaultProps = {
  maxTimeSlotLength: 4,
  formErrors: {},
};

ScheduleRow.propTypes = {
  formErrors: PropTypes.instanceOf(Object),
  checkbox: PropTypes.instanceOf(Object).isRequired,
  radio: PropTypes.instanceOf(Object).isRequired,
  startTime: PropTypes.instanceOf(Array).isRequired,
  endTime: PropTypes.instanceOf(Array).isRequired,
  isMultiTimeSlot: PropTypes.bool.isRequired,
  maxTimeSlotLength: PropTypes.number,
};

export default ScheduleRow;
